import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import loginAction from "../../Actions/loginAction";

export const useOauthData = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "", show: false });
  const [isLoading, setIsLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [disableOnWrong, setDisableOnWrong] = useState(false);
  const [cartoonPhase, setCartoonPhase] = useState("idle");
  const [passwordFeedback, setPasswordFeedback] = useState("idle");
  const [cartoonActivity, setCartoonActivity] = useState("idle");
  const inactivityTimeout = useRef();
  const passwordInputRef = useRef(null);

  const navigate = useNavigate();

  // Wave on mount
  useEffect(() => {
    setCartoonActivity("wave");
    const waveTimer = setTimeout(() => setCartoonActivity("idle"), 1800);
    return () => clearTimeout(waveTimer);
  }, []);

  // Show welcome message after short delay
  useEffect(() => {
    const timer = setTimeout(() => setShowWelcome(true), 700);
    return () => clearTimeout(timer);
  }, []);

  // Boredom timer
  useEffect(() => {
    function setBored() {
      setCartoonActivity("bore");
    }
    clearTimeout(inactivityTimeout.current);
    if (cartoonPhase === "idle" || cartoonPhase === "typing-username") {
      inactivityTimeout.current = setTimeout(setBored, 120000);
    }
    return () => clearTimeout(inactivityTimeout.current);
  }, [username, password, cartoonPhase]);

  // Reset boredom on any activity
  useEffect(() => {
    function activity() {
      if (cartoonActivity === "bore" || cartoonActivity === "wave") {
        setCartoonActivity("idle");
      }
    }
    window.addEventListener("keydown", activity);
    window.addEventListener("mousedown", activity);
    window.addEventListener("mousemove", activity);
    return () => {
      window.removeEventListener("keydown", activity);
      window.removeEventListener("mousedown", activity);
      window.removeEventListener("mousemove", activity);
    };
    // eslint-disable-next-line
  }, [cartoonActivity]);

  // Cartoon phase based on active field
  useEffect(() => {
    if (document.activeElement?.id === "username") {
      setCartoonPhase("typing-username");
      setCartoonActivity("typing-username");
    } else if (document.activeElement?.id === "password") {
      setCartoonPhase("typing-password");
      setCartoonActivity("hide-eyes");
    } else if (!username && !password) {
      setCartoonPhase("idle");
      setCartoonActivity("idle");
    }
    // eslint-disable-next-line
  }, [username, password]);

  const handleUsernameFocus = () => {
    setCartoonPhase("typing-username");
    setCartoonActivity("typing-username");
  };
  const handlePasswordFocus = () => {
    setCartoonPhase("typing-password");
    setCartoonActivity("hide-eyes");
  };
  const handleBlur = () => {
    setCartoonPhase("idle");
    setCartoonActivity("idle");
  };

  // Reset password feedback when password changes
  useEffect(() => {
    setPasswordFeedback("idle");
    if (disableOnWrong) setDisableOnWrong(false);
    // eslint-disable-next-line
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (disableOnWrong) return;

    // console.log("[oAuthData] ▶ handleSubmit triggered");
    // console.log("[oAuthData] Username entered:", username);

    if (!username.trim() || !password.trim()) {
      // console.warn("[oAuthData] ⚠ Validation failed: empty fields");
      setMessage({ text: "Please fill in all fields", type: "warning", show: true });
      setPasswordFeedback("idle");
      return;
    }

    setIsLoading(true);
    setMessage({ text: "", type: "", show: false });
    // console.log("[oAuthData] Calling loginAction...");

    const result = await loginAction(username, password);
    // console.log("[oAuthData] loginAction result:", result);

    if (!result || !result.success) {
      // console.warn("[oAuthData] ⚠ Login failed:", result?.message);
      setMessage({
        text: result?.message || "Invalid username or password. Please try again.",
        type: "error",
        show: true,
      });
      setIsLoading(false);
      setDisableOnWrong(true);
      setPasswordFeedback("angry");
      setTimeout(() => {
        if (passwordInputRef.current) passwordInputRef.current.focus();
      }, 200);
      return;
    }

    // Success path
    const { data } = result;
    // console.log("[oAuthData] ✅ Login success. User:", data);
    // console.log("[oAuthData] Role detected:", data?.role);

    setPasswordFeedback("smile");
    setMessage({ text: "Login successful! Redirecting...", type: "success", show: true });

    setTimeout(() => {
      // console.log("[oAuthData] 🔀 Redirecting based on role:", data?.role);
      navigate("/admin/dashboard");
    }, 1000);
  };

  const togglePasswordVisibility = () => setShowPassword((val) => !val);

  // Auto-hide non-success messages after 5s
  useEffect(() => {
    if (message.show && message.type !== "success") {
      const timer = setTimeout(() => setMessage((m) => ({ ...m, show: false })), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const isButtonDisabled = isLoading || disableOnWrong;

  return {
    cartoonPhase,
    cartoonActivity,
    username,
    setUsername,
    password,
    setPassword,
    showPassword,
    message,
    setMessage,
    isLoading,
    showWelcome,
    passwordInputRef,
    handleUsernameFocus,
    handlePasswordFocus,
    handleBlur,
    togglePasswordVisibility,
    isButtonDisabled,
    handleSubmit,
    passwordFeedback,
  };
};
